import styled from '@emotion/styled';
import { DARK_BLUE, GREEN } from '../colors';
import { ReactComponent as CalculatorSVG } from '../images/calculator.svg';
import { ReactComponent as LogoCNC } from '../images/cnc-logo.svg';
import { ReactComponent as LogoJyros } from '../images/logo-jyros-white.svg';
import { DateText, MainTitle, SubRow, SubText } from './SimpleReportCover';
import useReportCoverTexts from './useReportCoverTexts';

const CoverLayout = styled.div`
  background: ${DARK_BLUE};
  width: 210mm;
  height: 297mm;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  position: relative;
  padding: 0;
  overflow: hidden;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  padding: 2.5rem 2.5rem 0 2.5rem;
`;

const CalculatorIconContainer = styled.div`
  width: 180px;
  height: 180px;
  margin-left: 40px;
  svg {
    transform: rotate(-25deg);
  }
  svg polygon {
    fill: ${GREEN} !important;
    stroke-width: 0 !important;
  }
  svg path {
    fill: #fff !important;
  }
`;

const CenterBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const YearOutline = styled.div`
  font-size: 18rem;
  line-height: 18rem;
  font-family: Ibrand, sans-serif;
  font-weight: 700;
  color: #38384f;
  text-shadow: -0.5px -0.5px 0 #fff, 0.5px -0.5px 0 #fff, -0.5px 0.5px 0 #fff,
    0.5px 0.5px 0 #fff;
  text-align: center;
  margin-bottom: -4rem;
  letter-spacing: 0.04em;
`;

const Divider = styled.div`
  width: 2px;
  height: 3rem;
  background: #fff;
`;

const BottomRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 4rem;
  align-items: center;
  width: 100%;
  padding: 0 2.5rem 2.5rem 2.5rem;
  padding-bottom: 6rem;
`;

const LogoBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  width: 20%;
  svg path,
  svg polygon {
    stroke-width: 0 !important;
    fill: #fff !important;
  }
`;

const JyrosSubtitle = styled.div`
  color: ${GREEN};
  font-size: 0.8rem;
  margin-left: -1rem;
  margin-right: -1rem;
  text-align: center;
  font-family: Ibrand, sans-serif;
`;

const CNCBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  width: 20%;

  svg path,
  svg polygon {
    stroke-width: 0 !important;
    fill: #fff !important;
  }
`;

const CNCSubtitle = styled.div`
  color: ${GREEN};
  font-size: 1rem;
  margin-left: -2rem;
  margin-right: -2rem;
  text-align: center;
  font-family: Ibrand, sans-serif;
`;

const CompleteReportCover = ({
  reportDate,
  year,
  projectName,
}: {
  reportDate: Date;
  year: number | undefined;
  projectName?: string | undefined;
}) => {
  const { title, subtitle, companyText, jyrosSubtitle, homologationText } =
    useReportCoverTexts('complete', year);

  return (
    <CoverLayout>
      <TopRow>
        <CalculatorIconContainer>
          <CalculatorSVG />
        </CalculatorIconContainer>
        <DateText>
          {Intl.DateTimeFormat('fr-FR', { dateStyle: 'short' }).format(
            reportDate,
          )}
        </DateText>
      </TopRow>
      <CenterBlock>
        <MainTitle>{title}</MainTitle>
        {year && <YearOutline>{year}</YearOutline>}
        <SubRow>
          <SubText>{subtitle}</SubText>
          <SubText>{companyText}</SubText>
          {projectName && <SubText>{projectName}</SubText>}
        </SubRow>
      </CenterBlock>
      <BottomRow>
        <LogoBlock>
          <LogoJyros />
          <JyrosSubtitle>{jyrosSubtitle}</JyrosSubtitle>
        </LogoBlock>
        <Divider style={{ height: '5rem' }} />
        <CNCBlock>
          <CNCSubtitle>{homologationText}</CNCSubtitle>
          <LogoCNC />
        </CNCBlock>
      </BottomRow>
    </CoverLayout>
  );
};

export default CompleteReportCover;
